import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FaUpload } from 'react-icons/fa';
import { addDriver, extractWorkerReason } from './api';

const QUOTE_STYLES = ['DOUBLE_QUOTE', 'BRACKET', 'BACKTICK'];

const emptyCustom = {
  typeName: '',
  driverClassName: '',
  displayName: '',
  jdbcUrlTemplate: '',
  defaultPort: 0,
  defaultSchema: '',
  identifierQuoteStyle: 'DOUBLE_QUOTE',
};

/**
 * Jar-upload dialog for one driver flavor. `flavor` is either a catalog entry object
 * (pre-picked from the inline catalog table on the Drivers page) or the string 'CUSTOM'
 * (free-form driver definition + jar). The old two-step Add Driver modal's flavor picker
 * is gone — the main page shows the whole catalog, so this dialog only does step 2.
 */
const UploadDriverModal = ({ show, flavor, onHide, onSuccess }) => {
  const isCustom = flavor === 'CUSTOM';
  const entry = isCustom ? null : flavor;

  const [file, setFile] = useState(null);
  const [custom, setCustom] = useState(emptyCustom);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setSubmitError(null);
    setFile(null);
    setCustom(emptyCustom);
  }, [show]);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!file) {
      setSubmitError({ headline: 'Choose a jar file to upload.' });
      return;
    }
    if (isCustom) {
      if (!custom.typeName || !custom.driverClassName) {
        setSubmitError({ headline: 'typeName and driverClassName are required for Custom.' });
        return;
      }
      if (!custom.jdbcUrlTemplate) {
        setSubmitError({ headline: 'Provide a JDBC URL template.' });
        return;
      }
    }
    setSubmitting(true);
    try {
      const fields = isCustom ? { ...custom } : {};
      await addDriver(isCustom ? 'CUSTOM' : entry?.flavorId, file, fields);
      if (onSuccess) onSuccess();
      onHide();
    } catch (e) {
      setSubmitError({
        headline: e?.message || 'Upload failed',
        detail: extractWorkerReason(e?.body),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderCustomFields = () => (
    <>
      <Form.Group className="mb-2">
        <Form.Label>Type name</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. CLICKHOUSE"
          value={custom.typeName}
          onChange={(e) => setCustom({ ...custom, typeName: e.target.value })}
        />
        <Form.Text className="text-muted">
          Uppercase identifier. Used in API calls and the data source picker.
        </Form.Text>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Driver class</Form.Label>
        <Form.Control
          type="text"
          placeholder="com.example.jdbc.Driver"
          value={custom.driverClassName}
          onChange={(e) => setCustom({ ...custom, driverClassName: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Display name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Friendly name shown in the UI"
          value={custom.displayName}
          onChange={(e) => setCustom({ ...custom, displayName: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>JDBC URL template</Form.Label>
        <Form.Control
          type="text"
          placeholder="jdbc:foo://{host}:{port}/{database}"
          value={custom.jdbcUrlTemplate}
          onChange={(e) => setCustom({ ...custom, jdbcUrlTemplate: e.target.value })}
        />
        <Form.Text className="text-muted">Must contain {'{host}'} and {'{database}'}.</Form.Text>
      </Form.Group>
      <Row>
        <Col xs={6}>
          <Form.Group className="mb-2">
            <Form.Label>Default port</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={65535}
              value={custom.defaultPort}
              onChange={(e) => setCustom({ ...custom, defaultPort: Number(e.target.value) })}
            />
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group className="mb-2">
            <Form.Label>Default schema</Form.Label>
            <Form.Control
              type="text"
              value={custom.defaultSchema}
              onChange={(e) => setCustom({ ...custom, defaultSchema: e.target.value })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Group className="mb-2">
        <Form.Label>Identifier quote style</Form.Label>
        <Form.Control
          as="select"
          value={custom.identifierQuoteStyle}
          onChange={(e) => setCustom({ ...custom, identifierQuoteStyle: e.target.value })}
        >
          {QUOTE_STYLES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Form.Control>
      </Form.Group>
    </>
  );

  const renderPreview = () => (
    <div className="border rounded p-2 mb-2 small">
      <div>
        <strong>{entry?.displayName}</strong>
      </div>
      <div className="text-muted">Class: {entry?.driverClass}</div>
      <div className="text-muted">URL: {entry?.jdbcUrlTemplate}</div>
      <div className="text-muted">
        Port: {entry?.defaultPort} · Schema:{' '}
        {entry?.defaultSchema || '(none)'} · Quoting:{' '}
        {entry?.identifierQuoteStyle}
      </div>
      <div className="text-muted mt-1">
        These defaults can be edited later via Configure on the row, or overridden per-connection
        in the data source editor's Advanced section.
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUpload className="mr-2" />
          {isCustom ? 'Custom driver' : `Upload ${entry?.displayName || ''} driver`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isCustom && entry && renderPreview()}
        {isCustom && renderCustomFields()}
        <Form.Group className="mb-2 mt-2">
          <Form.Label>Driver jar</Form.Label>
          <Form.Control
            type="file"
            accept=".jar"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Form.Group>
        {submitError && (
          <Alert variant="danger">
            <div>{submitError.headline}</div>
            {submitError.detail && (
              <div
                className="mt-1 small"
                style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {submitError.detail}
              </div>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting || !file}>
          {submitting && <Spinner animation="border" size="sm" className="mr-2" />}
          Add driver
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadDriverModal;
